/* eslint-disable class-methods-use-this */
import { getCookie, setLS, getLS } from 'hobbit-base/modules/utils';
/**
 * 用户身份认证模块(请使用this.$user)
 */
export default class User {
  constructor($config, $fetch, $ctx) {
    this.$config = $config;
    this.$fetch = $fetch;
    this.$ctx = $ctx;
    this.tokenCheckTime = new Date(0);
    this.appId = null;
    this.aiToken = null;

    if (this.$ctx.match({ appProtocol: '2022' })) {
      if (this.$ctx.match({ appName: ['VIPKID PARENT', 'VIPKID STUDY', 'TIGERSCHOOL', 'VIPKID KOREA'] })) {
        window.$hobbit.$bridge
          .call({
            name: 'user/getRequestInfo',
            version: 3,
            timeout: 1e4,
          })
          .then((resp) => {
            if (!resp.errorCode && resp.data?.data?.studentId) {
              this.vkStudentId(resp.data?.data?.studentId);
            }

            if (!resp.errorCode && resp.data?.data?.parentId) {
              this.vkUserId(resp.data?.data?.parentId);
            }
          });
      }
    } else {
      window.$hobbit.$bridge
        .call({
          name: 'user/getRequestInfo',
          version: 3,
          timeout: 1e4,
        })
        .then((resp) => {
          if (!resp.errorCode && resp.data?.data?.studentId) {
            this.vkStudentId(resp.data?.data?.studentId);
          }

          if (!resp.errorCode && resp.data?.data?.parentId) {
            this.vkUserId(resp.data?.data?.parentId);
          }
        });
    }
  }

  async refreshAIToken() {
    const resp = await this.$fetch.post({
      url: '/rest/gw/api/masti/refreshToken',
      timeout: 10e3,
    });
    if (!resp.errorCode) {
      this.aiToken = resp.data?.data?.accessToken;
      this.appId = resp.data?.data?.appId;
    }
  }

  /**
   * 获取老师ID
   */
  vkUserId(v) {
    if (v) {
      setLS(this.$config.user.userIdKey, Number(v));
      return v;
    }

    const id = getLS(this.$config.user.userIdKey);
    if (id) return Number(id);
    return null;
  }

  /**
   * 持久化学生ID
   */
  vkStudentId(v) {
    if (v) {
      setLS(this.$config.user.studentIdKey, Number(v));
      return v;
    }

    const id = getLS(this.$config.user.studentIdKey);
    if (id) return Number(id);
    return null;
  }

  /**
   * 获取鉴权Token
   */
  vkToken() {
    return getCookie(this.$config.user.tokenKey);
  }

  /**
   * 检查token是否有效
   */
  async checkToken(tk) {
    if (!tk) {
      return false;
    }

    const resp = await this.$fetch.get({
      url: '/rest/gw/api/intern/user/checkLoginStatus',
      timeout: 3000,
    });

    if (!resp.errorCode && resp.data) {
      this.tokenCheckTime = new Date().getTime();
      return true;
    }
    return false;
  }

  /**
   * 是否已登录
   */
  async isLogin(autoRedirect = false, backurl) {
    const token = this.vkToken();
    if (token) {
      const nowTime = new Date().getTime();
      if (nowTime - this.tokenCheckTime < this.$config.user.tokenRecheckSeconds * 1e3) {
        // token免检期内
        return true;
      }

      // 距离上次token检测时间已经超过阀值，需要重新检查
      const passed = await this.checkToken(token);
      if (passed) {
        return true;
      }

      // 删除无效的token和登录id
      this.vkToken(null);
    }

    // 无token，判断是否要自动跳转登录页
    if (autoRedirect) {
      this.gotoLogin(backurl);
    }

    return false;
  }

  /**
   * 跳转登录页
   */
  // eslint-disable-next-line class-methods-use-this
  gotoLogin(backurl, ani) {
    if (this.$ctx.isApp()) {
      if (this.$ctx.match({ appProtocol: '2022' })) {
        return window.$hobbit.$bridge.call({
          name: 'loginRequired',
          params: {
            showClose: 1,
          },
          timeout: 1e4,
        });
      }
      return window.$hobbit.$bridge.call({
        name: 'user/loginRequired',
        version: 3,
        params: {
          showClose: 1,
        },
        timeout: 1e4,
      });
    }

    if (!backurl) {
      backurl = window.location.href;
    }

    const signinUrl = window.$hobbit.$theme.signinUrl.format({ backurl: encodeURIComponent(backurl) });
    return window.$hobbit.$app.pageTo(signinUrl, ani);
  }

  /**
   * 获取所有孩子信息
   */
  async getStudentInfo(
    fullInfo = false,
    preventDefaultAuthCheck = false,
    cache = {
      online: null,
    },
    checkEmptyStudents = true,
  ) {
    const resp = await this.$fetch.get({
      url: '/rest/gw/api/class/student/info',
      timeout: 1e4,
      preventDefaultAuthCheck,
      cache,
    });
    if (resp.errorCode) {
      return [];
    }
    const studentList = resp.data?.data ?? [];
    // 统一拦截没孩子进入补填孩子信息页面逻辑
    if (checkEmptyStudents && studentList.length === 0) {
      window.$hobbit.$app.pageTo(`${window.$hobbit.$theme.addchildURL}?from=supplement&backurl=${encodeURIComponent(window.location.href)}`);
      return [];
    }
    // 如果需要完整信息，则继续追加家庭接口获取的学生字段
    if (fullInfo) {
      const family = await this.getFamilyDetail();
      if (family) {
        const today = new Date();
        for (const student of studentList) {
          Object.assign(
            student,
            family.studentList.find((s) => String(s.id) === String(student.studentId)),
          );
          // 补充年龄属性
          if (student.birthday) {
            const birthday = Date.from(student.birthday);
            student.age = parseInt((today.getFullYear() * 12 + today.getMonth() - (birthday.getFullYear() * 12 + birthday.getMonth())) / 12, 10);
          }
        }
      }
    }
    return studentList;
  }

  // 新版获取level
  async getStudentLevel(preventDefaultAuthCheck = false) {
    const resp = await this.$fetch.get({
      url: '/rest/gw/api/class/student/level',
      params: { studentId: this.vkStudentId() },
      timeout: 1e4,
      preventDefaultAuthCheck,
      cache: { online: null }, // 内存级缓存
    });

    if (resp.errorCode) {
      return null;
    }
    const { level = 'level 1' } = resp.data?.data ?? {};

    return `${level.split(' ')[1]}`;
  }

  /**
   * 获取当前学生Level
   */
  async vkStudentLevel(courseId, preventDefaultAuthCheck = false) {
    const resp = await this.$fetch.get({
      url: '/rest/gw/api/class/student/info',
      params: { courseId },
      timeout: 1e4,
      preventDefaultAuthCheck,
      cache: { online: null }, // 内存级缓存
    });

    if (resp.errorCode) {
      return null;
    }
    const studentList = resp.data?.data ?? [];
    let currentStudentInfo = null;
    if (studentList && studentList.length) {
      const currentStudentId = this.vkStudentId();
      if (currentStudentId) {
        currentStudentInfo = studentList.find((s) => String(s.studentId) === String(currentStudentId));
      }
    }

    return currentStudentInfo?.level
      ? {
        currentLevel: `${currentStudentInfo.level.split(' ')[1]}`, // 当前level
        nextBookLevel: currentStudentInfo.nextBookLevel, // 下级level ，约课相关需要使用此level，展示需要使用level
      }
      : {
        currentLevel: '',
        nextBookLevel: '',
      };
  }

  /**
   * 获取当前已选中的孩子信息
   */
  async getCurrentStudentInfo(fullInfo = false, preventDefaultAuthCheck = false) {
    const studentInfo = await this.getStudentInfo(fullInfo, preventDefaultAuthCheck);
    if (studentInfo && studentInfo.length) {
      const currentStudentId = this.vkStudentId();
      if (currentStudentId) {
        const finded = studentInfo.find((s) => String(s.studentId) === String(currentStudentId));
        if (finded) return finded;
      }
      this.vkStudentId(studentInfo[0].studentId);
      return studentInfo[0];
    }
    return null;
  }

  /**
   * 获取家庭信息
   */
  async getFamilyDetail(autoRedirect = false, ignoreCache) {
    if (await this.isLogin(autoRedirect)) {
      const resp = await this.$fetch.get({
        url: '/rest/gw/api/intern/family/getDetail',
        timeout: 1e4,
        preventDefaultAuthCheck: false,
        cache: ignoreCache
          ? undefined
          : {
            online: 600 * 1e3,
            offline: 3600 * 1e3,
          },
      });
      if (!resp.errorCode && resp.data?.data?.parent?.id) {
        this.vkUserId(resp.data.data.parent.id);
        const studentList = resp.data?.data?.studentList;
        const currentStudentId = this.vkStudentId();
        if (!studentList?.length) {
          this.vkStudentId(null);
        } else if (!studentList.some((item) => item.id === currentStudentId)) {
          this.vkStudentId(studentList[0].id);
        }
        return resp.data.data;
      }
    }
    return null;
  }

  /**
   * 清除学生信息缓存
   */
  updateStudentInfoCache() {
    this.$fetch.removeCache('/rest/gw/api/class/student/info');
  }

  /**
   * 更新家庭信息缓存
   */
  updateFamilyDetailCache() {
    this.$fetch.removeCache('/rest/gw/api/intern/family/getDetail');
  }
}
